// ─────────────────────────────────────────────────────────────
// ScenarioSelector – choose / refine / save a risk *scenario*
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../utils/authFetch';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Radio, Group, Stack, Button, Textarea,
  Text, Title, ActionIcon, Chip, MultiSelect, Collapse, Divider
} from '@mantine/core';
import {
  IconRobot, IconCheck, IconRefresh, IconEdit, IconTarget, IconCpu, IconArrowsDiagonal2,
  IconBulb, IconBook, IconDatabase
} from '@tabler/icons-react';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
// RTK async thunks for scenario operations
import {
  fetchRiskScenarios,
  fetchRiskScenariosWithRAG,
  refineScenario,
} from '../redux/slices/riskScenariosSlice';
// Keep complex thunk as legacy (handles API calls + multiple state updates)
import { promoteScenarioToSaved } from '../redux/actions/riskScenarioActions';
import { useAuth0 } from '@auth0/auth0-react';
import { notifications } from '@mantine/notifications';
import { v4 as uuid } from 'uuid';          // fallback ids if needed

/* props:
     – modelNarratives is required so we can send use-case / model / business narratives
*/
/*──────────────── Quick-Refine presets ────────────────*/

  const PRESETS = [
  { label: 'More specific',   icon: <IconTarget size={14} color="#495057"/> },
  { label: 'More technical',  icon: <IconCpu size={14} color="#495057"/> },
    { label: 'Shorten',         icon: <IconArrowsDiagonal2 size={14} color="#495057"/> },
    { label: 'Clarify',         icon: <IconBulb size={14} color="#495057"/> },
    { label: 'Plain language',  icon: <IconBook size={14} color="#495057"/> },
  ];

  function QuickRefineBar({ onSelect }) {
      return (
        <Group spacing="xs" mb="xs">
          {PRESETS.map(p => (
            <Chip 
              key={p.label} 
              value={p.label} 
              radius="sm" 
              size="xs"
              variant="outline"
              leftSection={p.icon}
              onClick={() => onSelect(p.label)}
              style={{ cursor: 'pointer' }}
            >
              {p.label}
            </Chip>
          ))}
        </Group>
      );
    }
export default function ScenarioSelector({
  modelId,
  assessmentUuid,
  riskArea,
  source,
  modelNarratives          // ← NEW prop (already used in catalogue)
}) {
  const dispatch         = useDispatch();
  const { user } = useAuth0();
  const [selected, setSelected] = useState(null);     // the picked scenario
  const [editOn , setEditOn ] = useState(null);       // scenarioId being edited
  const [editTxt, setEditTxt] = useState('');         // text in textarea
  
  // Catalogue selection state
  const [availableCatalogues, setAvailableCatalogues] = useState([]);
  const [selectedCatalogues, setSelectedCatalogues] = useState([]);
  const [showCatalogueOptions, setShowCatalogueOptions] = useState(false);
  const [cataloguesLoading, setCataloguesLoading] = useState(false);
  const [cataloguesFetched, setCataloguesFetched] = useState(false);

  /* ------------------------------------------------------------------ */
  /*  Redux selector – scenarios live in riskScenarios slice            */
  /* ------------------------------------------------------------------ */
  const scenarios = useSelector(
    s => s.riskScenarios?.[modelId]?.[riskArea]?.[source.name] || []
  );
  const loading = useSelector(s => s.riskScenarios?._loading || false);

  /* ------------------------------------------------------------------ */
  /*  1)  Generate (or regenerate) scenarios - LLM based               */
  /* ------------------------------------------------------------------ */
  const generate = async () => {
    /* build the "card" object expected by λ */
    const card = {
      id      : source.id || uuid(),
      title   : source.name,
      section : riskArea,
      context : source.description || '',
      causes  : source.causesByValue || {}   // FULL causes blob
    };

    await dispatch(
      fetchRiskScenarios(modelId, assessmentUuid, card, modelNarratives)
    );
  };

  /* ------------------------------------------------------------------ */
  /*  1b) Fetch available catalogues from Supabase                     */
  /* ------------------------------------------------------------------ */
  const fetchCatalogues = useCallback(async () => {
    if (cataloguesFetched) return; // Avoid duplicate fetches
    
    setCataloguesLoading(true);
    try {
      const res = await authFetch('/.netlify/functions/listCataloguesSupabase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}) // List all catalogues
      });
      
      if (res.ok) {
        const { categories } = await res.json();
        const catalogueOptions = categories?.map(cat => ({
          value: cat,
          label: cat.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        })) || [];
        setAvailableCatalogues(catalogueOptions);
      }
    } catch (error) {
      console.error('Failed to fetch catalogues:', error);
    } finally {
      setCataloguesLoading(false);
      setCataloguesFetched(true);
    }
  }, [cataloguesFetched]);

  /* ------------------------------------------------------------------ */
  /*  Load catalogues on mount to determine UI state                    */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    fetchCatalogues();
  }, [fetchCatalogues]);

  /* ------------------------------------------------------------------ */
  /*  1c) Generate scenarios using risk catalogues - RAG based         */
  /* ------------------------------------------------------------------ */
  const generateWithCatalogues = async () => {
    const useAllCatalogues = selectedCatalogues.length === 0;
    
    /* build the "card" object expected by λ */
    const card = {
      id      : source.id || uuid(),
      title   : source.name,
      section : riskArea,
      context : source.description || '',
      causes  : source.causesByValue || {}   // FULL causes blob
    };

    await dispatch(
      fetchRiskScenariosWithRAG(modelId, assessmentUuid, card, modelNarratives, selectedCatalogues, useAllCatalogues)
    );
  };

  /* ------------------------------------------------------------------ */
  /*  2)  Refine one scenario                                           */
  /* ------------------------------------------------------------------ */
  const runRefine = async (scenario) => {
    await dispatch(
      refineScenario({
        modelId,
        assessmentUuid,
        sectionName     : riskArea,
        category        : source.name,
        originalName    : scenario.scenarioId,
        originalRiskText: scenario.scenarioText,
        userInstruction : editTxt
      })
    );
    notifications.show({
      color  : 'blue',
      title  : 'Refined!',
      message: 'Scenario updated successfully',
      icon   : <IconRobot size={16} />
    });
    setEditOn(null);
    setEditTxt('');
  };

  /* ------------------------------------------------------------------ */
  /*  3)  Promote chosen scenario → saved risks                         */
  /* ------------------------------------------------------------------ */
  const saveSelected = () => {
    if (!selected) return;
    dispatch(
      promoteScenarioToSaved({
        modelId,
        assessmentUuid,
        riskArea,
        sourceName: source.name,
        scenario : selected,
        owner    : {
          name   : user?.name  || 'Unknown',
          email  : user?.email || '',
          picture: user?.picture || ''
        }
      })
    );
    notifications.show({
      color  : 'green',
      title  : 'Saved!',
      message: 'Risk created & saved successfully',
      icon   : <IconCheck size={14} />
    });
  };

  /* ------------------------------------------------------------------ */
  /*  UI                                                                */
  /* ------------------------------------------------------------------ */


  return (
    <Card withBorder radius="md" p="xl">
      {/* header */}
      <Group mb="md" position="apart">
        <Title order={5}>AI-Generated Risk Scenarios</Title>
        <ActionIcon
          onClick={generate}
          variant="light"
          title="Generate / refresh scenarios"
        >
          <IconRefresh size={16} />
        </ActionIcon>
      </Group>

      {/* 1)  empty state */}
      {scenarios.length === 0 && (
        <Stack align="center" gap="lg">
          <Text>No scenarios yet.</Text>
          
          {/* Quick LLM Generation Section */}
          <Card withBorder p="lg" radius="md" style={{ backgroundColor: '#f8f9fa' }}>
            <Stack align="center" gap="md">
              <Text size="sm" fw={500} c="dark">Quick Generation</Text>
              <Button
                color="blue"
                variant="filled"
                size="md"
                leftSection={<AIGeneratingIcon isGenerating={loading} size={16} color="white" />}
                onClick={generate}
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Generate with LLM'}
              </Button>
            </Stack>
          </Card>

          {/* Visual Separator */}
          <Divider
            label="OR"
            labelPosition="center"
            size="md"
            color="gray.4"
            style={{ width: '100%' }}
          />

          {/* Catalogue Generation Section */}
          <Card withBorder p="lg" radius="md" style={{ backgroundColor: '#f8f9fa' }}>
            <Stack align="center" gap="md">
              <Text size="sm" fw={500} c="dark">Catalogue Generation</Text>
              
              {cataloguesLoading ? (
                <Stack align="center" gap="xs">
                  <Text size="sm" c="dimmed">Loading catalogues...</Text>
                </Stack>
              ) : cataloguesFetched && availableCatalogues.length === 0 ? (
                <>
                  {/* No catalogues available */}
                  <Stack align="center" gap="sm" ta="center">
                    <IconDatabase size={32} color="#6c757d" />
                    <Text size="sm" fw={500} c="dimmed">No risk catalogues available</Text>
                    <Text size="xs" c="dimmed" style={{ maxWidth: 280 }}>
                      To generate from catalogues, first embed your risk frameworks and standards.
                    </Text>
                    <Button
                      variant="light"
                      color="purple"
                      size="sm"
                      leftSection={<IconBook size={16} />}
                      onClick={() => {
                        // Navigate to file uploader or show instructions
                        window.open('/upload', '_blank');
                      }}
                    >
                      Upload Risk Catalogues
                    </Button>
                    <Text size="xs" c="gray" style={{ maxWidth: 300 }} ta="center">
                      Need catalogues to get started? Check the file uploader to embed NIST, ISO 27001, or custom frameworks.
                    </Text>
                  </Stack>
                </>
              ) : (
                <>
                  {/* Catalogues available */}
                  <Button
                    color="purple"
                    variant="light"
                    size="md"
                    leftSection={<IconDatabase size={16} />}
                    onClick={() => {
                      setShowCatalogueOptions(!showCatalogueOptions);
                      if (!showCatalogueOptions) {
                        fetchCatalogues();
                      }
                    }}
                    disabled={loading}
                  >
                    Generate from Risk Catalogues
                  </Button>
                  
                  <Collapse in={showCatalogueOptions}>
                    <Stack gap="md" align="center" style={{ minWidth: '350px' }}>
                      <Divider label="Select Catalogues" labelPosition="center" size="sm" />
                      
                      <MultiSelect
                        label="Available Risk Catalogues"
                        placeholder="Leave empty to use all catalogues"
                        data={availableCatalogues}
                        value={selectedCatalogues}
                        onChange={setSelectedCatalogues}
                        size="sm"
                        w="100%"
                        searchable
                        clearable
                        description="Select specific catalogues or leave empty to search across all available frameworks"
                      />
                      
                      <Text size="xs" c="dimmed" ta="center">
                        {selectedCatalogues.length === 0 
                          ? `Will search across all ${availableCatalogues.length} available catalogues`
                          : `Using ${selectedCatalogues.length} selected catalogue${selectedCatalogues.length !== 1 ? 's' : ''}`
                        }
                      </Text>
                      
                      <Button
                        size="md"
                        variant="filled"
                        color="purple"
                        onClick={generateWithCatalogues}
                        disabled={loading}
                        leftSection={<AIGeneratingIcon isGenerating={loading} size={16} color="white" />}
                      >
                        {loading ? 'Generating...' : 'Generate Scenarios'}
                      </Button>
                    </Stack>
                  </Collapse>
                </>
              )}
            </Stack>
          </Card>
        </Stack>
      )}

      {/* 2)  list of scenarios */}
      {scenarios.length > 0 && (
        <Radio.Group
          orientation="vertical"
          spacing="lg"
          value={selected?.scenarioId || null}
          onChange={(id) => {
            const sc = scenarios.find(s => s.scenarioId === id);
            setSelected(sc || null);
          }}
        >
          {scenarios.map(sc => (
            <Card key={sc.scenarioId} withBorder p="sm" radius="sm">
              <Stack spacing="xs">
                {/* main line */}
                <Group position="apart">
                  <Radio value={sc.scenarioId} label={sc.scenarioText} />
                  <ActionIcon
                    variant="light"
                    title="Refine this scenario"
                    onClick={() => {
                      setEditOn(sc.scenarioId);
                      setEditTxt(sc.scenarioText);
                    }}
                  >
                    <IconEdit size={14} />
                  </ActionIcon>
                </Group>

                {/* inline-edit panel */}
                {editOn === sc.scenarioId && (
                   <>
                      <QuickRefineBar onSelect={setEditTxt} />
                  {/* <Stack> */}
                    <Textarea
                      value={editTxt}
                      onChange={e => setEditTxt(e.currentTarget.value)}
                      minRows={3}
                    />
                    <Group position="right">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => { setEditOn(null); setEditTxt(''); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        color="blue"
                        variant="light"
                        leftSection={<AIGeneratingIcon isGenerating={loading} size={14} color="#228be6" />}
                        disabled={!editTxt.trim() || loading}
                        onClick={() => runRefine(sc)}
                      >
                        {loading ? 'Refining...' : 'Refine'}
                      </Button>
                    </Group>
                  {/* </Stack> */}
                  </>
                )}

                {/* rationale (always show) */}
                {sc.rationale && (
                  <Text size="xs" c="dimmed" fs="italic" align="left">
                      Why: {sc.rationale}
                   </Text>

                )}

                {/* catalogue source information */}
                {sc.cataloguesUsed && sc.cataloguesUsed.length > 0 && (
                  <Group gap="xs" mt="xs">
                    <Text size="xs" fw={500} c="purple">
                      From catalogues:
                    </Text>
                    {sc.cataloguesUsed.map((catalogue, idx) => (
                      <Chip key={idx} size="xs" variant="light" color="purple">
                        {catalogue}
                      </Chip>
                    ))}
                  </Group>
                )}

                {sc.generationMethod === 'LLM_Generation' && (
                  <Text size="xs" c="blue" mt="xs">
                    🤖 LLM Generated
                  </Text>
                )}
              </Stack>
            </Card>
          ))}
        </Radio.Group>
      )}

      {/* footer */}
      <Group position="right" mt="md">
        <Button
          disabled={!selected}
          leftSection={<IconCheck size={16} />}
          onClick={saveSelected}
        >
          Save Selected
        </Button>
      </Group>
    </Card>
  );
}